import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';
import { reloadForSchool } from '@/services/schoolSwitch';
import { useUserStore } from '@/store/userStore';

/**
 * Mirrors a logout performed in one browser tab to every other open tab of the
 * same origin.
 *
 * When a tab logs out it runs `localStorage.clear()`, and the `storage` event
 * fires in all *other* tabs (never in the tab that made the change). We listen
 * for the auth token going away and tear this tab's session down too, then send
 * it to the login page — so logging out anywhere logs out everywhere at once.
 *
 * Call once from an always-mounted root component (App.vue).
 */
export function useCrossTabAuthSync() {
  const userStore = useUserStore();
  const router = useRouter();
  const canFollowSchool = () => userStore.isLoggedIn &&
    (userStore.currentUser as { __typename?: string } | null)?.__typename !== 'Student';

  const onStorage = (event: StorageEvent) => {
    if (event.key === LocalStorageKeys.currentSchoolId && event.newValue &&
      event.newValue !== event.oldValue && canFollowSchool()) {
      void reloadForSchool({ id: event.newValue, name: 'Your selected school' }, true);
      return;
    }

    // `localStorage.clear()` fires with key === null; an explicit
    // `removeItem(token)` fires with key === token and a null newValue.
    // Ignore every other storage change.
    const tokenRemoved =
      event.key === null || (event.key === LocalStorageKeys.token && !event.newValue);
    if (!tokenRemoved) return;

    // Act only when the token is genuinely gone AND this tab still believes it
    // is signed in. The `isLoggedIn` guard also prevents the clear()-echo
    // between tabs from looping: once a tab logs out it stops reacting.
    if (localStorage.getItem(LocalStorageKeys.token) || !userStore.isLoggedIn) return;

    userStore.logout();
    if (router.currentRoute.value.name !== 'login') {
      router.replace({ name: 'login' });
    }
  };

  const onPageShow = (event: PageTransitionEvent) => {
    const schoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
    if (event.persisted && schoolId && userStore.currentSchool?.id &&
      schoolId !== String(userStore.currentSchool.id) && canFollowSchool()) {
      void reloadForSchool({ id: schoolId }, true);
    }
  };
  onMounted(() => {
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
  });
  onBeforeUnmount(() => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('pageshow', onPageShow);
  });
}
